<!--
  DatesFilterWithCustomRange renders a filter with dropdown date options and reads
  from URL query params `start_date` and `end_date`, and emits to parent component
  when the date range is changed
-->

<script>
import { iFilter, iIcon } from '@inscribe/signal';
import { formatAsDateString, subDays, differenceInDays } from '@/utils/date';

const OPTION = {
  PAST_DAY: 'Past day',
  PAST_7_DAYS: 'Past 7 days',
  PAST_30_DAYS: 'Past 30 days',
  PAST_90_DAYS: 'Past 90 days',
  CUSTOM_RANGE: 'Custom range',
};

export default {
  components: {
    iFilter,
    iIcon,
  },
  props: {
    params: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      range: {
        start: new Date(),
        end: new Date(),
      },
    };
  },
  computed: {
    value() {
      const startDate = this.params.start_date || this.params.startDate;
      const endDate = this.params.end_date || this.params.endDate;
      const customRange = this.params.custom_range || this.params.customRange;
      if (!startDate || !endDate) {
        return [];
      }
      const diffDays = differenceInDays(new Date(startDate), new Date(endDate));
      if (customRange) {
        return [OPTION.CUSTOM_RANGE];
      }
      if (diffDays <= 2) {
        return [OPTION.PAST_DAY];
      } else if (diffDays <= 8) {
        return [OPTION.PAST_7_DAYS];
      } else if (diffDays <= 31) {
        return [OPTION.PAST_30_DAYS];
      } else if (diffDays <= 91) {
        return [OPTION.PAST_90_DAYS];
      }

      return [];
    },
    options() {
      return Object.values(OPTION).map((o) =>
        o === OPTION.CUSTOM_RANGE ? { label: o, range: true } : { label: o },
      );
    },
    customRangeLabel() {
      return `${this.$moment(this.range.start).format(
        'DD/MM/YYYY',
      )} - ${this.$moment(this.range.end).format('DD/MM/YYYY')}`;
    },
    rangeString() {
      if (!this.rangeSet) {
        return 'Select date range';
      } else {
        return this.customRangeLabel;
      }
    },
    rangeSet() {
      var startM = this.$moment(this.range.start);
      var startE = this.$moment(this.range.end);
      return !startM.isSame(startE, 'day');
    },
  },
  async mounted() {
    if (this.params.custom_range) {
      this.syncRangeValues();
    }
  },
  methods: {
    onSelectionChanged(updated) {
      let startDate = null;
      let endDate = null;
      let customRange = null;
      const today = new Date();
      const todayStr = formatAsDateString(today);

      const updatedOption = updated.selection[0]?.label;
      if (updatedOption === OPTION.PAST_DAY) {
        startDate = formatAsDateString(subDays(today, 1));
        endDate = todayStr;
      } else if (updatedOption === OPTION.PAST_7_DAYS) {
        startDate = formatAsDateString(subDays(today, 7));
        endDate = todayStr;
      } else if (updatedOption === OPTION.PAST_30_DAYS) {
        startDate = formatAsDateString(subDays(today, 30));
        endDate = todayStr;
      } else if (updatedOption === OPTION.PAST_90_DAYS) {
        startDate = formatAsDateString(subDays(today, 90));
        endDate = todayStr;
      } else if (updatedOption === OPTION.CUSTOM_RANGE) {
        startDate = formatAsDateString(this.range.start);
        endDate = formatAsDateString(this.range.end);
        customRange = true;
      }

      this.$emit('dates-filter-updated', {
        startDate,
        endDate,
        customRange,
      });
    },
    syncRangeValues() {
      this.range = {
        start: this.$moment(this.params.start_date || this.params.startDate),
        end: this.$moment(this.params.end_date || this.params.endDate),
      };
    },
  },
};
</script>

<template>
  <iFilter
    label="Dates"
    :options="options"
    :searchable="false"
    :max-selectable="1"
    :value="value"
    :custom-range="true"
    :custom-range-label="customRangeLabel"
    class="dates-filter"
    @selection-changed="onSelectionChanged"
  >
    <template slot="custom-range">
      <v-date-picker
        v-model="range"
        is-range
        :max-date="new Date()"
        :popover="{ placement: 'right-start' }"
      >
        <template #default="{ inputEvents }">
          <div class="dates-filter__range d-flex i-gap-4">
            <div class="i-form-input-group">
              <div class="input-action float-left">
                <i-icon symbol="small-calendar-date" :size="16" />
              </div>
              <input
                :class="{
                  'i-text-neutral-110': !rangeSet,
                  'i-text-neutral-120': rangeSet,
                }"
                class="i-form-input has-start-icon"
                :value="rangeString"
                v-on="inputEvents.start"
              />
            </div>
          </div>
        </template>
      </v-date-picker>
    </template>
  </iFilter>
</template>

<style lang="scss" scoped>
.dates-filter {
  &__range {
    width: 100%;

    .i-form-input-group {
      width: 100%;
    }

    input {
      width: 100%;
    }
  }
}
</style>
